import Seo from "../components/Seo";
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { SlidersHorizontal, X } from "lucide-react";

const SIZE_OPTS = ["Малък", "Среден", "Голям"];
const COLOR_OPTS = ["Синьо", "Златно", "Зелено", "Червено", "Бяло", "Черно", "Сиво", "Кафяво"];
const STYLE_OPTS = ["Класически", "Абстрактни"];
const ROOM_OPTS = ["Дневна", "Спалня", "Офис", "Трапезария"];
const PRICE_OPTS = [["До 100 €", "100"], ["100 – 250 €", "250"], ["250 – 500 €", "500"], ["Над 500 €", "9999"]];
const SORTS = [["Препоръчани", "recommended"], ["Цена ↑", "price_asc"], ["Цена ↓", "price_desc"], ["Най-нови", "newest"]];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const get = (k) => params.get(k) || "";
  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    const q = {
      category: get("category"), style: get("style"), room: get("room"),
      color: get("color"), size_group: get("size"), search: get("search"),
      max_price: get("max") || undefined, sort: get("sort") || "recommended", limit: 48,
    };
    Object.keys(q).forEach((k) => !q[k] && delete q[k]);
    api.get("/products", { params: q }).then(({ data }) => { setProducts(data.items); setTotal(data.total); setLoading(false); });
  }, [params]); // eslint-disable-line

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const Dropdown = ({ label, paramKey, options }) => (
    <div className="relative">
      <select value={get(paramKey)} onChange={(e) => setParam(paramKey, e.target.value)}
              className="appearance-none bg-white border border-stone px-4 py-2.5 pr-9 text-sm text-navy cursor-pointer focus:border-gold outline-none min-w-[130px]"
              data-testid={`filter-${paramKey}`}>
        <option value="">{label}</option>
        {options.map((o) => Array.isArray(o)
          ? <option key={o[1]} value={o[1]}>{o[0]}</option>
          : <option key={o} value={o}>{o}</option>)}
      </select>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gold text-xs">▾</span>
    </div>
  );

  const activeFilters = ["category", "style", "room", "color", "size", "max", "search"].filter((k) => get(k));

  return (
    <div data-testid="catalog-page">
      <Seo title="Картини" path="/kartini" description="Разгледайте оригинални картини с маслени и акрилни бои – пейзажи, портрети, натюрморти, абстрактни и шедьоври. Ръчно рисувани от Силва Пенева." />
      {/* header */}
      <div className="bg-navy text-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16 text-center">
          <h1 className="font-serif text-4xl md:text-5xl text-white">Картини</h1>
          <p className="text-ivory/70 mt-3">{total} оригинални произведения · ръчно рисувани с маслени бои</p>
        </div>
      </div>

      {/* category chips */}
      <div className="border-b border-stone bg-ivory sticky top-[72px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex gap-2 overflow-x-auto no-scrollbar">
          <button onClick={() => setParam("category", "")}
                  className={`whitespace-nowrap px-4 py-1.5 text-sm border ${!get("category") ? "bg-navy text-white border-navy" : "border-stone text-navy"}`}
                  data-testid="cat-chip-all">Всички</button>
          {categories.map((c) => (
            <button key={c.slug} onClick={() => setParam("category", c.slug)}
                    className={`whitespace-nowrap px-4 py-1.5 text-sm border ${get("category") === c.slug ? "bg-navy text-white border-navy" : "border-stone text-navy hover:border-gold"}`}
                    data-testid={`cat-chip-${c.slug}`}>{c.name}</button>
          ))}
        </div>
      </div>

      {/* filter bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => setShowFilters((s) => !s)} className="md:hidden flex items-center gap-2 border border-navy px-4 py-2.5 text-sm text-navy" data-testid="mobile-filters-toggle">
            <SlidersHorizontal size={16} /> Филтри
          </button>
          <div className={`${showFilters ? "flex" : "hidden"} md:flex flex-wrap gap-3 w-full md:w-auto`}>
            <Dropdown label="Размер" paramKey="size" options={SIZE_OPTS} />
            <Dropdown label="Цена" paramKey="max" options={PRICE_OPTS} />
            <Dropdown label="Цвят" paramKey="color" options={COLOR_OPTS} />
            <Dropdown label="Стил" paramKey="style" options={STYLE_OPTS} />
            <Dropdown label="Помещение" paramKey="room" options={ROOM_OPTS} />
          </div>
          <div className="ml-auto">
            <Dropdown label="Сортирай" paramKey="sort" options={SORTS} />
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {activeFilters.map((k) => (
              <button key={k} onClick={() => setParam(k, "")} className="flex items-center gap-1.5 text-xs bg-stone text-navy px-3 py-1.5" data-testid={`active-filter-${k}`}>
                {get(k)} <X size={12} />
              </button>
            ))}
            <button onClick={() => setParams({})} className="text-xs text-gold underline" data-testid="clear-filters">Изчисти всички</button>
          </div>
        )}
      </div>

      {/* grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/5] bg-stone animate-pulse" />)}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 text-charcoal/50" data-testid="no-results">Няма намерени картини. Опитайте друг филтър.</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
